<template>
  <div class="lessonEditor">
    <section v-show="!loadingPage">
      <BaseMenuBar
        :nameList="[studyDetails.name, interventionDetails.name, lessonDetails.name]"
        :idList="[studyId, interventionId]"
      ></BaseMenuBar>

      <BaseTabs
        :tabDetails="tabDetails"
        :selectedView="selectedView"
        @show-view="showView"
      ></BaseTabs>

      <BaseSelectLanguage
        v-if="selectedView != 2 && lessonDetails.locales"
        :localesArray="lessonDetails.locales"
        :selectedLanguage2="selectedLanguageForTranslation"
        :isTranslationMode="isTranslationMode"
        @close-notification="closeNotification"
        @change-language2="changeLanguage2"
        @change-translationmode="changeTranslationMode"
      ></BaseSelectLanguage>

      <section v-show="selectedView === 0" :id="lessonDetailsFormId" class="wrapper">
        <button type="submit" disabled style="display: none"></button>
        <section v-show="!loadingLanguage">
          <!-- normal language (left) -->
          <section :class="{col: isTranslationMode, sec50:isTranslationMode}">
            <LessonEditorDetails
              :lessonDetails="lessonDetails"
              :translation="currentTranslation"
              :isTranslation="false"
              :isTranslationMode="isTranslationMode"
              :selectedLanguageForTranslation="selectedLanguageForTranslation"
              :studyId="studyId"
              @close-notification="closeNotification"
              @change-attribute="changeAttributeForDetails"
              @show-modal="showModal"
            ></LessonEditorDetails>
          </section>

          <!-- translation language (right) -->
          <section :class="{col: isTranslationMode, sec50:isTranslationMode}">
            <section v-if="isTranslationMode && getIsEditable">
              <LessonEditorDetails
                :lessonDetails="lessonDetails"
                :translation="currentTranslation2"
                :isTranslation="true"
                :isTranslationMode="isTranslationMode"
                :selectedLanguageForTranslation="selectedLanguageForTranslation"
                :studyId="studyId"
                @close-notification="closeNotification"
                @change-attribute="changeAttributeForDetails"
                @show-modal="showModal"
              ></LessonEditorDetails>
            </section>
          </section>

          <section class="wrapper">
            <button type="submit" id="buttonSaveLessonDetails" :disabled="isSaved" @click="saveLessonDetails">
              {{ 'generalTranslation.save' | translate }}
            </button>
          </section>
        </section>

        <i class="fa fa-spinner fa-spin fa-2x loadData" v-if="loadingLanguage"></i>
      </section>

      <section v-if="!loadingElements && !loadingIntDetails" id="lessonElements">
        <LessonEditorElements
          v-show="selectedView === 1"
          :editorModalsVisible="warningChangesVisible"
          :lessonDetails="lessonDetailsComplete"
          :interventionDetails="interventionDetails"
          :formChanged="formChanged"
          :isTranslationMode="isTranslationMode"
          :selectedLanguageForTranslation="selectedLanguageForTranslation"
          :isDiary="false"
          :isSkill="false"
          :studyId="studyId"
          :currentPageLessonElements="currentPageLessonElements"
          @close-notification="closeNotification"
          @show-modal="showModal"
          @route-to="routeTo"
        ></LessonEditorElements>
      </section>

      <section v-show="selectedView === 2" id="translationStatus" class="wrapper">
        <div v-if="lessonDetails.translation_status != null" key="progressDiv">
          <section v-for="language in Object.keys(lessonDetails.translation_status)" :key="language">
            <section class="wrapper">
              <section class="col sec3">
                {{language}}
              </section>
              <section class="col sec25">
                <progress :value="lessonDetails.translation_status[language]" max="1"></progress>
              </section>
            </section>
          </section>
        </div>
        <div v-else key="noProgressDiv">
          {{ 'interventionTranslation.noProgress' | translate }}
        </div>
      </section>
    </section>
    <i class="fa fa-spinner fa-spin fa-2x loadData" v-if="loadingPage"></i>

    <BaseModal
      v-if="warningChangesVisible"
      id="changesModal"
      :bodyText="warningText"
      :isLeaveSiteWarning="true"
      :leftButtonText="$t('interventionTranslation.leave')"
      @close-modal="resolvePromise"
    ></BaseModal>

    <BaseModal
      v-if="deleteLanguageModalVisible"
      id="deleteLessonLanguageModal"
      :headerText="deleteLanguageHeaderText"
      :bodyText="deleteLanguageText"
      :leftButtonText="$t('interventionTranslation.ok')"
      @close-modal="deleteLanguageModal"
    ></BaseModal>

    <BaseNotification ref="notificationRef"></BaseNotification>
  </div>
</template>

<script>
import Vue from 'vue';
import {mapGetters,mapMutations} from 'vuex';
import router from '../router';
import BaseMenuBar from './BaseMenuBar.vue';
import BaseTabs from './BaseTabs.vue';
import BaseNotification from './BaseNotification.vue';
import BaseModal from './BaseModal.vue';
import BaseSelectLanguage from './BaseSelectLanguage.vue';
import LessonEditorDetails from './LessonEditorDetails.vue';
import LessonEditorElements from './LessonEditorElements.vue';
import httpHelper from '../mixins/httpHelper';

export default {
  name: 'LessonEditor',

  components: {
    BaseMenuBar,
    BaseTabs,
    BaseNotification,
    BaseModal,
    BaseSelectLanguage,
    LessonEditorDetails,
    LessonEditorElements
  },

  mixins: [httpHelper],

  props: ['view'],

  data: function(){
    return {
      selectedView: 1,
      warningChangesVisible: false,
      lessonDetails: [],
      currentTranslation: "",
      currentTranslation2: "",
      isSaved: false,
      nextRouteName: '',
      loadingLanguage: true,
      otherModalVisible: false,
      selectedLanguageForTranslation: "",
      formChanged: false, //true if lesson details changed by user & not yet saved
      isTranslationMode: false,
      lessonDetailsComplete: [],
      interventionDetails: [],
      selectMediaVisible: false,
      studyDetails: [],
      loadingPage: true,
      loadingElements: true,
      copyElementsBool: false,
      resolvePromise: null,
      rejectPromise: null,
      currentPageLessonElements: 1,
      loadingIntDetails: false,
      lessonDetailsFormId: "lessonDetailsForm",
      deleteLanguageModalVisible: false,
      deleteLanguage: "",
    }
  },

  computed: {
    ...mapGetters([
      'getSelectedLanguage',
      'getMyRoleForStudy',
      'getPageRefreshBool',
      'getNotificationText',
      'getFormElementsChanged',
      'getConditionEditor',
      'getPageRefreshDetails',
      'getUserId',
      'getIsEditable'
    ]),

    studyId: function(){
      return Number(this.interventionDetails.study_id)
    },

    interventionId: function(){
      return Number(this.lessonDetails.intervention_id)
    },

    lessonId: function(){
      return Number(this.$route.params.lessonId)
    },

    warningText: function(){
      var string = Vue.i18n.translate('generalTranslation.unsavedChanges') + ": ";
      if(this.formChanged){
        string += Vue.i18n.translate('lessonTranslation.lessonDetails');
      }
      if(this.getFormElementsChanged && this.nextRouteName != 'conditionEditor'){
        if(this.formChanged){
          string += ", ";
        }
        string += Vue.i18n.translate('lessonTranslation.lessonElements');
      }
      return string
    },

    deleteLanguageText: function() {
      return Vue.i18n.translate('lessonTranslation.deleteLanguageConfirm', { language: this.deleteLanguage });
    },

    deleteLanguageHeaderText: function() {
      return Vue.i18n.translate('lessonTranslation.deleteLanguageConfirmHeader');
    },

    tabDetails: function(){
      return [
        {text: Vue.i18n.translate('lessonTranslation.lessonDetails'), show: true, id: "details"},
        {text: Vue.i18n.translate('lessonTranslation.lessonElements'), show: true, id: "elements"},
        {text: Vue.i18n.translate('interventionTranslation.translationProgress'), show: true, id: "progress"},
      ]
    }
  },

  watch: {
    '$route.params.lessonId': function(){
      if(this.copyElementsBool){
        //get new data after copy elements in different lesson
        this.copyElementsBool = false;
        this.loadingPage = true;
        this.loadingLanguage = true;
        this.loadingIntDetails = true;
        this.loadingElements = false;
        this.SET_FORMELEMENTSCHANGED(true);
        this.getDetailsOfLesson(true);
      }else{
        //get new data when going back from lesson where elements were copied to to lesson where elements where copied from
        this.loadingPage = true;
        this.loadingLanguage = true;
        this.loadingElements = true;
        this.getElementsAndDetails();
      }
    },

    getNotificationText(newVal) {
      if (newVal == "") {
        return;
      }
      if (newVal.type === "error") {
        this.isSaved = false;
        this.loadingLanguage = false;
      } else if (newVal.type === "success") {
        this.isSaved = false;
      }
    },

    getSelectedLanguage(){
      this.setCurrentTranslations(false);
    },

    selectedLanguageForTranslation(newVal){
      if(newVal != ""){
        this.setCurrentTranslations(true);
      }else if(this.currentTranslation2 != ""){
        this.lessonDetails.translations.push(this.currentTranslation2);
        this.currentTranslation2 = "";
      }
    },
  },

  mounted(){
    if(this.getNotificationText.text != Vue.i18n.translate('lessonTranslation.createLessonSuccess')){
      this.closeNotification();
    }

    this.SET_PREVIEWANSWERS({});

    if((this.getPageRefreshBool || this.getConditionEditor) && this.getPageRefreshDetails.id === this.lessonId){
      this.SET_CONDITIONEDITOR(false);
      this.lessonDetails = this.getPageRefreshDetails.lessonDetails;
      this.lessonDetailsComplete = JSON.parse(JSON.stringify(this.lessonDetails));
      this.interventionDetails = this.getPageRefreshDetails.interventionDetails;
      this.studyDetails = this.getPageRefreshDetails.studyDetails;
      this.formChanged = this.getPageRefreshDetails.formChanged;
      this.loadingElements = false;
      this.loadingIntDetails = false;
      this.init();
      this.loadingPage = false;
      this.selectedView = this.getPageRefreshDetails.view;
      this.currentPageLessonElements = this.getPageRefreshDetails.currentPage
    }else{
      if(this.view != undefined){
        this.selectedView = this.view;
      }
      this.getElementsAndDetails();
    }
    this.SET_CONDITIONEDITOR(false);
    this.SET_PAGEREFRESHBOOL(false);
    this.SET_PAGEREFRESHDETAILS([]);

    window.addEventListener('beforeunload', this.beforeunloadFunction);
    window.addEventListener('unload', this.unloadFunction);

    if(document.getElementById(this.lessonDetailsFormId) != null){
      document.getElementById(this.lessonDetailsFormId).addEventListener("input", this.changeFormHasChanged);
    }
  },

  beforeRouteUpdate (to, from, next) {
    this.nextRouteName = to.name;
    if(to.name === from.name && !this.copyElementsBool && (this.formChanged || this.getFormElementsChanged)){
      var self = this;
      this.showWarningModal()
      .then(ok => {
        self.closeModal();
        if(ok){
          self.formChanged = false;
          if(to.name != "conditionEditor"){
            self.SET_FORMELEMENTSCHANGED(false);
          }
          next();
        }else{
          next(false);
        }
      });
    }else{
      next();
    }
  },

  beforeRouteLeave(to, _from, next){
    this.nextRouteName = to.name;
    this.SET_PAGEREFRESHBOOL(false);
    this.SET_PAGEREFRESHDETAILS([]);
    if(this.otherModalVisible || this.selectMediaVisible || (!this.getConditionEditor && to.name === "conditionEditor")){
      next(false);
    }else if(this.formChanged || (this.getFormElementsChanged && to.name != "conditionEditor")){
      var self = this;
      this.showWarningModal()
      .then(ok => {
        self.closeModal();
        if(ok){
          self.formChanged = false;
          if(to.params.oldLessonId === undefined && to.name === "interventionEditor"){
            to.params.oldLessonId = self.lessonId;
          }

          if(to.name === "conditionEditor"){
            var data = self.setRefreshData();
            self.SET_PAGEREFRESHDETAILS(data);
          }
          if(to.name != "conditionEditor"){
            self.SET_QUESTIONLABELLIST([]);
            self.SET_FORMELEMENTSCHANGED(false);
          }
          next();
        }else{
          next(false);
        }
      });
    }else{
      if(to.params.oldLessonId === undefined && to.name === "interventionEditor"){
        to.params.oldLessonId = this.lessonId;
      }

      if(to.name === "conditionEditor"){
        var data = this.setRefreshData();
        this.SET_PAGEREFRESHDETAILS(data);
      }
      this.formChanged = false;
      if(to.name != "conditionEditor"){
        this.SET_QUESTIONLABELLIST([]);
        this.SET_FORMELEMENTSCHANGED(false);
      }
      next();
    }
  },

  beforeDestroy(){
    if(document.getElementById(this.lessonDetailsFormId)){
      document.getElementById(this.lessonDetailsFormId).removeEventListener("input", this.changeFormHasChanged);
    }
    window.removeEventListener('beforeunload', this.beforeunloadFunction);
    window.removeEventListener('unload', this.unloadFunction);
  },

  methods:{
    ...mapMutations([
      'SET_NOTIFICATIONTEXT',
      'SET_SELECTEDLANGUAGE',
      'SET_PREVIEWANSWERS',
      'SET_PAGEREFRESHBOOL',
      'SET_FORMELEMENTSCHANGED',
      'SET_QUESTIONLABELLIST',
      'SET_PAGEREFRESHDETAILS',
      'SET_LESSONELEMENTSARR',
      'SET_CONDITIONEDITOR',
    ]),

    showWarningModal: function(){
      return new Promise((resolve, reject) => {
        this.warningChangesVisible = true;
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      })
    },

    getElementsAndDetails: function(){
      var self = this;
      this.requestLessonElements(this.lessonId, false, true)
      .then(function (response){
        self.SET_LESSONELEMENTSARR(response);
        self.loadingIntDetails = true;
        self.loadingElements = false;
        self.getDetailsOfLesson(true);
      })
      .catch(function (error){
        self.handleErrors(error, function(){ self.getElementsAndDetails() }, "studyDetails");
      });
    },

    getDetailsOfLessonAfterLanguageDelete: async function() {
      try {
        const response = await this.requestLessonDetails(this.lessonId);
        this.lessonDetails = response;
        this.lessonDetailsComplete = JSON.parse(JSON.stringify(this.lessonDetails));
        this.refreshLessonDetails();
      } catch (error) {
        this.handleErrors(error, function(){ this.getDetailsOfLessonAfterLanguageDelete() }, "studyDetails");
      }
    },

    getDetailsOfLesson: async function(initBool){
      try {
        const response = await this.requestLessonDetails(this.lessonId);
        if (initBool) {
          this.lessonDetails = response;
          this.init();
          this.lessonDetailsComplete = JSON.parse(JSON.stringify(this.lessonDetails));
          this.getInterventionAndStudy();
        }
      } catch (error) {
        const self = this;
        this.handleErrors(error, function() { self.getDetailsOfLesson(initBool) }, "studyDetails");
      }
    },

    getInterventionAndStudy: function(){
      var self = this;
      this.requestInterventionDetails(this.interventionId)
      .then(function (response){
        self.interventionDetails = response;
        self.loadingIntDetails = false;
        return self.requestCollaboratorsIncludingPermissions("page=0&id=" + self.getUserId, self.studyId, false, -1, -1)
      })
      .then(function (){
        return self.requestStudyDetails(self.studyId)
      })
      .then( function(response){
        self.studyDetails = response;
        self.loadingPage = false;
      })
      .catch(function (error){
        self.handleErrors(error, function(){ self.getInterventionAndStudy() }, "");
      });
    },

    init: function(){
      if(!this.lessonDetails.locales || this.lessonDetails.locales.length === 0){
        this.lessonDetails.locales = ["de", "en"];
      }

      if(!this.lessonDetails.locales.includes(this.getSelectedLanguage)){
        const language = (this.lessonDetails.locales.includes("de")) ? "de" : this.lessonDetails.locales[0];
        this.SET_SELECTEDLANGUAGE(language);
      }

      this.selectedLanguageForTranslation = "";
      this.isTranslationMode = false;

      this.setCurrentTranslations(false);
    },

    showModal: function(obj){
      switch (obj.type) {
        case "StudyMedia":
          this.selectMediaVisible = obj.value;
          break;
        case "LanguageError":
          this.SET_NOTIFICATIONTEXT({type: "error", text: Vue.i18n.translate('interventionTranslation.deleteSelectedLanguageWarning')});
          break;
        case "LessonPreview":
          this.setUpdatedLessonDetailsForPreview();
          break;
        case "any":
          this.otherModalVisible = obj.value;
          break;
        case "DeleteLanguage":
          this.deleteLanguage = obj.deleteLanguage;
          this.deleteLanguageModalVisible = true;
          break;
        default:
          break;
      }
    },

    async deleteLanguageModal(done) {
      if (done) {
        const locales = this.lessonDetails.locales.filter(l => l !== this.deleteLanguage);
        this.lessonDetails.locales = locales;
        this.lessonDetailsComplete.locales = locales;
        this.formChanged = true;
      }
      this.deleteLanguageModalVisible = false;
    },

    refreshLessonDetails: function(){
      if(!this.lessonDetails.locales.includes(this.getSelectedLanguage)){
        const language = (this.lessonDetails.locales.includes("de")) ? "de" : this.lessonDetails.locales[0];
        this.SET_SELECTEDLANGUAGE(language);
      }

      const language = this.getSelectedLanguage;

      if(this.selectedLanguageForTranslation === "" && this.currentTranslation2 != ""){
        this.lessonDetails.translations.push(this.currentTranslation2);
        this.currentTranslation2 = "";
      }

      let translation = "";
      const index = this.lessonDetails.translations.findIndex(elem => elem.locale === language);
      if(index != -1){
        translation = this.lessonDetails.translations[index];
        this.lessonDetails.translations.splice(index, 1);
      }else if(language != ""){
        translation = {
          locale: language,
          title: "",
          description: "",
        };
      }

      if(this.currentTranslation != ""){
        this.lessonDetails.translations.push(this.currentTranslation);
      }
      if(translation != ""){
        this.currentTranslation = translation;
      }
      this.loadingLanguage = false;
    },

    changeLanguage2: function(language){
      this.selectedLanguageForTranslation = language;
    },

    changeTranslationMode: function(isTranslationMode){
      this.isTranslationMode = isTranslationMode;
    },

    showView: function(view){
      this.closeNotification();
      this.selectedView = view;
    },

    changeAttributeForDetails: function(obj){
      this.closeNotification();
      if(obj.name === "locales"){
        obj.value.sort();
      }

      if (obj.name === "AddLanguage") {
        const locales = JSON.parse(JSON.stringify(this.lessonDetails.locales));
        locales.push(obj.value);
        locales.sort();
        this.lessonDetails.locales = locales;
        this.lessonDetailsComplete.locales = locales;
      } else if(obj.name != "title" && obj.name != "description") {
        this.lessonDetails[obj.name] = obj.value;
        this.lessonDetailsComplete[obj.name] = obj.value;
      } else if (!obj.isTranslation) {
        this.currentTranslation[obj.name] = obj.value;
      } else {
        this.currentTranslation2[obj.name] = obj.value;
      }

      this.formChanged = true;
    },

    changeFormHasChanged: function(){
      this.closeNotification();
      this.formChanged = true;
    },

    routeTo: function(obj){
      if(obj.name === 'conditionEditor'){
        this.currentPageLessonElements = obj.params.page;
        delete obj.params['page'];
        obj.params.lessonId = this.lessonId;
      }else if(obj.name === 'lessonEditor'){
        //route changes -> get new lessonDetails, interventionDetails, studyDetails
        //permissions and elements already set
        //in route.params.lessonId watch function
        this.copyElementsBool = true;
      }

      router.push(obj);
    },

    invalidLessonDetails() {
      let error = "";
      if(this.lessonDetails.name === ""){
        error += Vue.i18n.translate('generalTranslation.name');
      }

      if(this.currentTranslation.title === ""){
        error += ((error != "") ? ", " : "") + Vue.i18n.translate('interventionTranslation.title');
      }

      if(this.currentTranslation.description === ""){
        error += ((error != "") ? ", " : "") + Vue.i18n.translate('interventionTranslation.description');
      }

      if(this.currentTranslation2 != "" && this.currentTranslation2.title === ""){
        error += ((error != "") ? ", " : "") + Vue.i18n.translate('interventionTranslation.titleTranslation');
      }

      if(this.currentTranslation2 != "" && this.currentTranslation2.description === ""){
        error += ((error != "") ? ", " : "") + Vue.i18n.translate('interventionTranslation.descriptionTranslation');
      }
      return error;
    },

    saveLessonDetails: function(){
      const error = this.invalidLessonDetails();
      if (error !== "") {
        this.SET_NOTIFICATIONTEXT({type: "error", text: Vue.i18n.translate('adminUsersTranslation.errorFillIn') + error});
      }else{
        this.isSaved = true;
        this.SET_NOTIFICATIONTEXT({type: "load", text: Vue.i18n.translate('lessonTranslation.updateLessonDetailsLoad')});
        const details = JSON.parse(JSON.stringify(this.lessonDetails));
        if(!details.page_color){
          details.page_color = this.$defaultLessonPageColor;
        }
        // Add current translations in case they are missing
        if (this.currentTranslation != "" && details.translations.findIndex(t => t.locale === this.currentTranslation.locale) < 0) {
          details.translations.push(this.currentTranslation);
        }
        if (this.currentTranslation2 != "" && details.translations.findIndex(t => t.locale === this.currentTranslation2.locale) < 0) {
          details.translations.push(this.currentTranslation2);
        }

        this.updateDetails(details);
      }
    },

    updateDetails: function(details) {
      const self = this;
      this.updateLessonDetailsRequest(details, this.lessonId)
        .then(function() {
          self.SET_NOTIFICATIONTEXT({ type: "success", text: Vue.i18n.translate("lessonTranslation.updateLessonDetailsSuccess") });
          self.formChanged = false;
          self.getDetailsOfLesson(false);
        })
        .catch(function(error) {
          self.handleErrors(error, function() { self.updateDetails(details) }, "");
        });
    },

    setCurrentTranslations: function(isTranslation){
      const language = !isTranslation
        ? this.getSelectedLanguage
        : this.selectedLanguageForTranslation;

      if(this.selectedLanguageForTranslation === "" && this.currentTranslation2 != ""){
        this.lessonDetails.translations.push(this.currentTranslation2);
        this.currentTranslation2 = "";
      }

      let translation = "";
      const index = this.lessonDetails.translations.findIndex(elem => elem.locale === language);
      if(index != -1){
        translation = this.lessonDetails.translations[index];
        this.lessonDetails.translations.splice(index, 1);
      }else if(language != ""){
        translation = {
          locale: language,
          title: "",
          description: "",
        };
      }

      if(!isTranslation){
        if(this.currentTranslation != "" && this.lessonDetails.translations.findIndex(t => t.locale === this.currentTranslation.locale) < 0){
          this.lessonDetails.translations.push(this.currentTranslation);
        }
        if(translation != ""){
          this.currentTranslation = translation;
        }
      }else{
        if(this.currentTranslation2 != "" && this.lessonDetails.translations.findIndex(t => t.locale === this.currentTranslation2.locale) < 0){
          this.lessonDetails.translations.push(this.currentTranslation2);
        }
        if(translation != "" && this.selectedLanguageForTranslation != ""){
          this.currentTranslation2 = translation;
        }
      }
      this.loadingLanguage = false;
    },

    unloadFunction: function(){
      this.SET_PAGEREFRESHBOOL(true);
      this.closeNotification();
      this.isTranslationMode = false;
      this.currentPageLessonElements = 1;
      var data = this.setRefreshData();
      this.SET_PAGEREFRESHDETAILS(data);
    },

    setRefreshData: function(){
      //all translations in translations array
      var lessonDetailsArr = JSON.parse(JSON.stringify(this.lessonDetails));
      if(this.currentTranslation){
        lessonDetailsArr.translations.push(this.currentTranslation);
        this.currentTranslation = "";
      }

      if(this.currentTranslation2){
        lessonDetailsArr.translations.push(this.currentTranslation2);
        this.currentTranslation2 = "";
      }

      var data = {
        id: this.lessonId,
        lessonDetails: lessonDetailsArr,
        interventionDetails: this.interventionDetails,
        studyDetails: this.studyDetails,
        view: this.selectedView,
        formChanged: this.formChanged,
        currentPage: this.currentPageLessonElements
      };
      return data
    },

    beforeunloadFunction: function(e){
      if(this.formChanged || this.getFormElementsChanged){
        e.preventDefault();
        e.returnValue = '';
        return;
      }
      delete e['returnValue'];
    },

    setUpdatedLessonDetailsForPreview: function(){
      this.closeNotification();
      var lessonDetailsArr = JSON.parse(JSON.stringify(this.lessonDetails));
      if(this.currentTranslation){
        lessonDetailsArr.translations.push(JSON.parse(JSON.stringify(this.currentTranslation)));
      }

      if(this.currentTranslation2){
        lessonDetailsArr.translations.push(JSON.parse(JSON.stringify(this.currentTranslation2)));
        this.currentTranslation2 = "";
      }
      this.lessonDetailsComplete = lessonDetailsArr;
    },

    closeModal: function(){
      this.warningChangesVisible = false;
    },

    closeNotification: function(){
      this.$refs["notificationRef"].closeNotification();
    },
  }
}
</script>
